import i18n from '../i18n'
import { readActingUserId } from '../auth/actingUser'
import { getToast } from './toastBridge'
import {
  getApiErrorCode,
  getApiErrorField,
  getApiErrorStatus,
  isCanceled,
  isNetworkError,
  isOptimisticLock,
} from './apiError'

/**
 * Merkezde anlamlı metne çevrilen alan bazlı doğrulama hataları (`VALIDATION_ERROR` + `field`).
 *
 * <p>127 — görevin Stratejik Hedef / Katkı seçiminde **yeni** eklenen kalem bu arada pasife alınmış ya
 * da silinmişse sunucu 400 döner. Bu hata üç ayrı kayıt yolundan gelebilir (bağımsız görev oluşturma,
 * Görev Detay, toplantı/tutanak kaydı); eşleme ekran ekran tekrarlanırsa biri unutulur ve kullanıcı
 * "bir hata oluştu" görüp nedenini anlayamaz. Seçenek listesinin tazelenmesi `main.tsx`'te.
 */
const FIELD_ERROR_KEYS: Record<string, string> = {
  strategicObjectiveIds: 'actions.classification.errors.unavailable.strategicObjectiveIds',
  contributionIds: 'actions.classification.errors.unavailable.contributionIds',
}

/** Hata, görev formunun bayat bir Stratejik Hedef / Katkı seçeneğinden mi kaynaklandı? (127) */
export function isStaleClassificationOption(error: unknown): boolean {
  const field = getApiErrorField(error)
  // `in` DEĞİL: düz nesnede kalıtılmış anahtarlar da eşleşirdi (`field: "toString"` → tanımsız anahtar).
  return getApiErrorCode(error) === 'VALIDATION_ERROR' && field != null && Object.hasOwn(FIELD_ERROR_KEYS, field)
}

/**
 * Tek merkezi API hata → toast hunisi (019). TanStack Query global onError (QueryCache/MutationCache)
 * bunu çağırır; ekranlar kendi hata→mesaj eşlemesini tutmaz (Reuse Mandate, SC-006). Çapraz-kesen
 * sınıflar burada toast'lanır; 400 doğrulama ve domaine özel iş kodları ekrana bırakılır (FR-011).
 */
export interface NotifyMeta {
  /** true → bu hatada merkez hiç toast basmaz (ekran tamamen kendi ele alır). */
  suppressGlobalError?: boolean
  /** Bu iş kodları geldiğinde merkez toast basmaz (ekran alan hatası/özel mesaj gösterir). */
  handledCodes?: string[]
  /**
   * Vekil modunda **yasak** olan bir işlemin mesaj anahtarı (089).
   *
   * <p>Kullanıcı bir başkası adına çalışırken 403 alınırsa genel "yetkiniz yok" yerine bu metin
   * gösterilir. Sunucu, yasağı hedefin yetki kümesinden ilgili kodu **düşürerek** uyguladığı için
   * gövdede "hangi yetki eksikti" bilgisi yoktur; hangi eylemin denendiğini bilen tek taraf istemcidir.
   * Bu yüzden eşleme burada, **çağıranın beyanıyla** yapılır — sunucuda ikinci bir kontrol açılmaz.
   *
   * <p>Yalnız persona **aktifken** devreye girer: kendi kimliğinde alınan 403 gerçekten yetki
   * eksikliğidir ve genel metni hak eder.
   */
  proxyBlockedKey?: string
}

/** Interceptor'ın çapraz-kesen olarak ele aldığı (ör. 401) hatalar bu bayrağı taşır. */
function isHandledCrossCutting(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { handledCrossCutting?: boolean }).handledCrossCutting === true
}

// FR-014 dedup: aynı mesaj kısa pencerede tekrar gösterilmez.
const DEDUP_WINDOW_MS = 3000
let lastSummary: string | null = null
let lastAt = 0

function showError(summary: string): void {
  const now = Date.now()
  if (summary === lastSummary && now - lastAt < DEDUP_WINDOW_MS) return
  lastSummary = summary
  lastAt = now
  getToast()?.show({ severity: 'error', summary, life: 4000 })
}

/** Bir API hatasını uygun kullanıcı bildirimine (toast) eşler. */
export function notifyApiError(error: unknown, opts?: { meta?: NotifyMeta }): void {
  // Sessiz durumlar (ilk eşleşen kazanır).
  if (isCanceled(error) || isHandledCrossCutting(error)) return
  const meta = opts?.meta
  if (meta?.suppressGlobalError) return
  const code = getApiErrorCode(error)
  if (code && meta?.handledCodes?.includes(code)) return

  // Sınıf eşlemesi → tek kaynak i18n metni.
  const t = i18n.t.bind(i18n)
  const status = getApiErrorStatus(error)
  if (isOptimisticLock(error)) return showError(t('common.errors.optimisticLock'))
  if (status === 403) {
    if (meta?.proxyBlockedKey && readActingUserId() != null) {
      return showError(t(meta.proxyBlockedKey))
    }
    return showError(t('common.errors.forbidden'))
  }
  if (status === 404) return showError(t('common.errors.notFound'))
  // Metin hedef kolonun BAYT sınırına sığmadı (TEXT_TOO_LONG, 400). Genel "bir hata oluştu" burada
  // yanıltıcıdır: kullanıcı alanın KARAKTER sınırına uyduğu hâlde reddedilir ve nedenini anlayamaz —
  // Türkçe harfler UTF-8'de 2 bayttır (bkz. lib/textBytes.ts). Bu, istemci kuralının atlandığı
  // yollarda (ör. zod şeması olmayan formlar, doğrudan API çağrısı) devreye giren son ağdır.
  if (code === 'TEXT_TOO_LONG') return showError(t('common.errors.textTooLong'))
  if (isStaleClassificationOption(error)) {
    return showError(t(FIELD_ERROR_KEYS[getApiErrorField(error) as string]))
  }
  if (isNetworkError(error)) return showError(t('common.errors.network'))
  // Alan/doğrulama (400) hatalarını ekran, kendi kodunu `meta.handledCodes`'a ekleyerek üstlenir
  // (yukarıda susturulur, FR-011); üstlenilmeyen her hata (400 dahil) sessiz kalmaz → genel bildirim.
  showError(t('common.errors.generic'))
}

/** Test yardımcısı: dedup penceresi durumunu sıfırla. */
export function __resetNotifier(): void {
  lastSummary = null
  lastAt = 0
}
